import React, { useCallback } from 'react';
import { ModifyScheduleStateContentModel } from './ModifyScheduleStateContentModel';
import { Formik, FormikProps } from 'formik';
import { ScheduleData } from 'core/rtbCampaign/ScheduleData';
import { BatchEditCampaignPage } from 'components/BatchEditCampaign/BatchEditCampaignPage';
import i18n from 'i18n';
import { FormObserver } from 'components/common/form/FormObserver';
import { BatchEditCampaignSection } from 'components/BatchEditCampaign/BatchEditCampaignSection';
import { FormikDatePickerField } from 'components/common/form/field/DatePickerField';
import styles from './modifyScheduleStateContent.module.scss';
import moment from 'moment';
import { FormikCustomField } from 'components/common/form/field/CustomField';
import { FormikSwitchField } from 'components/common/form/field/SwitchField';

export const ModifyScheduleStateContent: React.FC<{
  model: () => ModifyScheduleStateContentModel;
}> = ({ model: useModel }) => {
  const {
    anyCampaignStarted,
    defaultScheduleData,
    minDate,
    maxDate,
    onDataChange,
    validateDateRange
  } = useModel();

  const renderStartDatePicker = useCallback(
    ({ values }: FormikProps<ScheduleData>) => (
      <>
        <FormikSwitchField
          name='enableStartDate'
          label={i18n.t('modifyScheduleState.enableStartDate')}
          disabled={anyCampaignStarted}
        />
        <FormikDatePickerField
          name='startDate'
          label={''}
          minDate={minDate}
          maxDate={moment
            .min(
              moment(values.endDate).subtract(1, 'hour').add(1, 'second'),
              moment(maxDate)
            )
            .format('YYYY-MM-DD HH:mm:ss')}
          popOverClassName={styles.datePicker}
          timeInterval={60}
          postComponent={
            <div className={styles.startDatePickerHint}>
              {i18n.t('modifyScheduleState.startDateCannotModifyHint')}
            </div>
          }
          fieldContentWidth={350}
          disabled={!values.enableStartDate}
        />
      </>
    ),
    [anyCampaignStarted, minDate, maxDate]
  );

  const renderEndDatePicker = useCallback(
    ({ values }: FormikProps<ScheduleData>) => {
      const validate = (endDate: string) => {
        return validateDateRange(values.startDate, endDate);
      };
      return (
        <>
          <FormikSwitchField
            name='enableEndDate'
            label={i18n.t('modifyScheduleState.enableEndDate')}
          />
          <FormikDatePickerField
            name='endDate'
            label={''}
            minDate={moment
              .max(
                moment(values.startDate).add(1, 'hour').subtract(1, 'second'),
                moment(minDate)
              )
              .format('YYYY-MM-DD HH:mm:ss')}
            maxDate={maxDate}
            popOverClassName={styles.datePicker}
            timeInterval={60}
            isEndTimePicker={true}
            disabled={!values.enableEndDate}
            validate={validate}
            placement='top'
          />
        </>
      );
    },
    [minDate, maxDate, validateDateRange]
  );

  const renderContent = useCallback(() => {
    return (
      <>
        <FormObserver onChange={onDataChange} />
        <BatchEditCampaignSection>
          <div style={{ padding: '30px' }}>
            <FormikCustomField
              label={i18n.t('campaignInfo.labels.dayRange')}
              render={renderStartDatePicker}
              fieldContentWidth={636}
            />
            <FormikCustomField
              label={''}
              render={renderEndDatePicker}
              fieldContentWidth={636}
            />
          </div>
        </BatchEditCampaignSection>
      </>
    );
  }, [onDataChange, renderStartDatePicker, renderEndDatePicker]);

  return (
    <BatchEditCampaignPage title={i18n.t('modifyScheduleState.title')}>
      <Formik
        initialValues={defaultScheduleData}
        onSubmit={onDataChange}
        initialTouched={{ endDate: true }}
        validateOnBlur={false}
        validateOnMount={true}
      >
        {renderContent}
      </Formik>
    </BatchEditCampaignPage>
  );
};
